import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import Services from '@/components/Services';
import HowItWorks from '@/components/HowItWorks';
import Features from '@/components/Features';
import ForBarbers from '@/components/ForBarbers';
import Cities from '@/components/Cities';
import AppDownload from '@/components/AppDownload';
import Footer from '@/components/Footer';
import BookingModal from '@/components/BookingModal';
import BarberSignupModal from '@/components/BarberSignupModal';
import AuthModal from '@/components/AuthModal';
import LiveTracker from '@/components/LiveTracker';
import CustomerDashboard from '@/pages/CustomerDashboard';
import BarberDashboard from '@/pages/BarberDashboard';
import { Toaster, toast } from 'sonner';

function App() {
  const [theme, setTheme] = useState('dark');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBarberModal, setShowBarberModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [activeBookingId, setActiveBookingId] = useState(null);
  const [user, setUser] = useState(null);
  const [ws, setWs] = useState(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');

    // Check for existing user session
    const savedUser = localStorage.getItem('barbergo_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }

    // Initialize WebSocket connection for real-time updates
    // WebSocket connection is optional - app works without it
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/${Date.now()}`;
    
    let websocket;
    try {
      websocket = new WebSocket(wsUrl);
      
      websocket.onopen = () => {
        console.log('WebSocket connected');
      };

      websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('WebSocket message error:', error);
        }
      };

      websocket.onerror = (error) => {
        console.log('WebSocket not available (non-critical)');
      };

      websocket.onclose = () => {
        console.log('WebSocket disconnected');
      };

      setWs(websocket);
    } catch (e) {
      console.log('WebSocket initialization skipped');
    }

    return () => {
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.close();
      }
    };
  }, []);

  const handleWebSocketMessage = (message) => {
    switch(message.type) {
      case 'new_booking':
        toast.info('New booking in your area!', {
          description: `${message.data.service} at ${message.data.time}`
        });
        break;
      case 'barber_joined':
        toast.success('New barber joined!', {
          description: `${message.data.name} is now available in ${message.data.city}`
        });
        break;
      case 'booking_status_updated':
        if (message.data.booking_id === activeBookingId) {
          toast.info('Booking status updated', {
            description: `Status: ${message.data.status}`
          });
        }
        break;
      default:
        break;
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  const handleBookNow = (service = null, data = null) => {
    setSelectedService(service);
    setBookingData(data);
    setShowBookingModal(true);
  };

  const handleBecomeBarber = () => {
    setShowBarberModal(true);
  };

  const handleBookingSuccess = (bookingId) => {
    setActiveBookingId(bookingId);
    setTimeout(() => setActiveBookingId(null), 60000); // Hide after 1 min
  };

  const handleSignIn = () => {
    setShowAuthModal(true);
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
  };

  const handleSignOut = () => {
    localStorage.removeItem('barbergo_user');
    setUser(null);
    toast.success('Signed out successfully');
  };

  const HomePage = () => (
    <>
      <Header 
        theme={theme} 
        toggleTheme={toggleTheme} 
        onBecomeBarber={handleBecomeBarber}
        onSignIn={handleSignIn}
        user={user}
      />
      <Hero onBookNow={handleBookNow} />
      <Services onBookNow={handleBookNow} />
      <HowItWorks />
      <Features />
      <ForBarbers onBecomeBarber={handleBecomeBarber} />
      <Cities />
      <AppDownload />
      <Footer />
    </>
  );

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/customer-dashboard" element={<CustomerDashboard />} />
          <Route path="/barber-dashboard" element={<BarberDashboard />} />
        </Routes>
        
        <BookingModal 
          open={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          selectedService={selectedService}
          initialData={bookingData}
          onSuccess={handleBookingSuccess}
        />
        
        <BarberSignupModal 
          open={showBarberModal}
          onClose={() => setShowBarberModal(false)}
        />

        <AuthModal 
          open={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />

        {activeBookingId && <LiveTracker bookingId={activeBookingId} />}
        
        <Toaster position="top-center" theme={theme} richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;